import React, { useState } from 'react';
import { Spin } from 'antd';
import Table, { ColumnProps } from 'antd/lib/table';
import {
    DownOutlined,
    ExclamationCircleFilled,
    UpOutlined,
} from '@ant-design/icons';
import { DegradationNote } from '~containers/analytics/kpis/degradation-cause';
import { couponsInRegion } from './coupon-stock';
import { buildRewardsInventory } from './rewards-inventory';
import type { RewardOptionRow, TierGroup } from './rewards-inventory';
import type { CampaignCouponStockState } from './use-campaign-coupon-stock';
import type { CampaignTierLadderState } from './use-campaign-tier-ladder';
import type { CampaignPromotionActivityState } from './use-campaign-promotion-activity';
import style from './rewards-inventory-table.module.scss';

/** An undefined rate (a zero denominator): a dash, matching the dash this
 * page already uses for other unavailable cells (`InventoryProgress`,
 * `RppPrizeCouponsPage`'s province fallback), never a false 0%. */
const UNDEFINED_RATE = '—';

const UNLIMITED_LABEL = 'Unlimited';

const formatRate = (rate: number | null): string =>
    rate == null ? UNDEFINED_RATE : `${(rate * 100).toFixed(1)}%`;

const formatCount = (value: number): string => value.toLocaleString();

/** `null` means no exact count exists for this row: it folds several
 * regional copies and the API served no `couponGroup` row covering just
 * them. Summing the copies would double-count anyone holding two, so the
 * dash stands in rather than a number the data cannot support. */
const formatMemberCount = (value: number | null): string =>
    value == null ? UNDEFINED_RATE : formatCount(value);

const formatQuantity = (value: number | null): string =>
    value == null ? UNLIMITED_LABEL : formatCount(value);

const formatRemaining = (value: number | null): string =>
    value == null ? UNDEFINED_RATE : formatCount(value);

type FlatRow =
    | { kind: 'group'; tierId: number; visits: number; open: boolean }
    | { kind: 'empty'; tierId: number }
    | { kind: 'option'; tierId: number; option: RewardOptionRow };

const rowKeyOf = (row: FlatRow): string => {
    if (row.kind === 'group') return `group-${row.tierId}`;
    if (row.kind === 'empty') return `empty-${row.tierId}`;
    return row.option.key;
};

const rowClassNameOf = (row: FlatRow): string => {
    if (row.kind === 'group') return style.group_row;
    if (row.kind === 'empty') return style.message_row;
    return style.option_row;
};

const buildFlatRows = (
    tiers: TierGroup[],
    openTierIds: ReadonlySet<number>
): FlatRow[] => {
    const rows: FlatRow[] = [];
    for (const tier of tiers) {
        const open = openTierIds.has(tier.tierId);
        rows.push({
            kind: 'group',
            tierId: tier.tierId,
            visits: tier.visits,
            open,
        });
        if (!open) continue;
        if (tier.rows.length === 0) {
            rows.push({ kind: 'empty', tierId: tier.tierId });
        } else {
            for (const option of tier.rows) {
                rows.push({ kind: 'option', tierId: tier.tierId, option });
            }
        }
    }
    return rows;
};

/** `null` = never toggled: use the default. Otherwise reconcile with the
 * current ladder, falling back only when every remembered id vanished;
 * an override that intersects, even to empty, is honoured as-is. */
const reconcileOpenTierIds = (
    override: ReadonlySet<number> | null,
    currentTierIds: ReadonlySet<number>,
    defaultTierId: number
): Set<number> => {
    if (override === null) return new Set([defaultTierId]);
    const intersection = new Set(
        [...override].filter((id) => currentTierIds.has(id))
    );
    if (override.size > 0 && intersection.size === 0) {
        return new Set([defaultTierId]);
    }
    return intersection;
};

const GroupRowLabel: React.FC<{
    visits: number;
    open: boolean;
    onToggle: () => void;
}> = ({ visits, open, onToggle }) => (
    <span
        className={style.group_label}
        role="button"
        tabIndex={0}
        aria-expanded={open}
        onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                onToggle();
            }
        }}
    >
        {open ? <UpOutlined aria-hidden /> : <DownOutlined aria-hidden />}
        {`Milestone ${formatCount(visits)}`}
    </span>
);

const EmptyTierMessage: React.FC = () => (
    <p className={style.empty_tier}>
        No coupon option in this tier serves the selected region.
    </p>
);

const NameCell: React.FC<{ row: RewardOptionRow }> = ({ row }) => (
    <div className={style.name_cell}>
        {row.image ? (
            <img src={row.image} alt="" className={style.thumbnail} />
        ) : (
            <div className={style.thumbnail_placeholder} />
        )}
        <span className={style.name_title}>{row.name}</span>
    </div>
);

const RemainingCell: React.FC<{ row: RewardOptionRow }> = ({ row }) =>
    row.isSoldOut ? (
        <span className={style.remaining_out} data-testid="remaining-out">
            <ExclamationCircleFilled aria-hidden />
            {formatCount(row.remaining ?? 0)}
            <span className={style.visually_hidden}>Sold out</span>
        </span>
    ) : (
        <>{formatRemaining(row.remaining)}</>
    );

const restColOnCell = (record: FlatRow) =>
    record.kind === 'option' ? {} : { colSpan: 0 };

const buildColumns = (
    onToggleTier: (tierId: number) => void
): ColumnProps<FlatRow>[] => {
    const columns: ColumnProps<FlatRow>[] = [
        {
            title: 'Coupon name',
            key: 'name',
            width: '26%',
            render: (_: unknown, record: FlatRow) => {
                if (record.kind === 'group') {
                    return (
                        <GroupRowLabel
                            visits={record.visits}
                            open={record.open}
                            onToggle={() => onToggleTier(record.tierId)}
                        />
                    );
                }
                if (record.kind === 'empty') return <EmptyTierMessage />;
                return <NameCell row={record.option} />;
            },
        },
        {
            title: 'Total coupons',
            key: 'quantity',
            onCell: restColOnCell,
            render: (_: unknown, record: FlatRow) =>
                record.kind === 'option'
                    ? formatQuantity(record.option.quantity)
                    : null,
        },
        {
            title: 'Issued coupons',
            key: 'couponsIssued',
            onCell: restColOnCell,
            render: (_: unknown, record: FlatRow) =>
                record.kind === 'option'
                    ? formatCount(record.option.couponsIssued)
                    : null,
        },
        {
            title: 'Redeemed coupons',
            key: 'couponsRedeemed',
            onCell: restColOnCell,
            render: (_: unknown, record: FlatRow) =>
                record.kind === 'option'
                    ? formatCount(record.option.couponsRedeemed)
                    : null,
        },
        {
            title: 'Coupon redemption rate',
            key: 'couponRedemptionRate',
            onCell: restColOnCell,
            render: (_: unknown, record: FlatRow) =>
                record.kind === 'option'
                    ? formatRate(record.option.couponRedemptionRate)
                    : null,
        },
        {
            title: 'Issued members',
            key: 'issuedMembers',
            onCell: restColOnCell,
            render: (_: unknown, record: FlatRow) =>
                record.kind === 'option'
                    ? formatMemberCount(record.option.issuedMembers)
                    : null,
        },
        {
            title: 'Redeemed members',
            key: 'redeemedMembers',
            onCell: restColOnCell,
            render: (_: unknown, record: FlatRow) =>
                record.kind === 'option'
                    ? formatMemberCount(record.option.redeemedMembers)
                    : null,
        },
        {
            title: 'Member redemption rate',
            key: 'memberRedemptionRate',
            onCell: restColOnCell,
            render: (_: unknown, record: FlatRow) =>
                record.kind === 'option'
                    ? formatRate(record.option.memberRedemptionRate)
                    : null,
        },
        {
            title: 'Expired',
            key: 'expiredCoupons',
            onCell: restColOnCell,
            render: (_: unknown, record: FlatRow) =>
                record.kind === 'option'
                    ? formatCount(record.option.expiredCoupons)
                    : null,
        },
        {
            title: 'Coupon exp. rate',
            key: 'expirationRate',
            onCell: restColOnCell,
            render: (_: unknown, record: FlatRow) =>
                record.kind === 'option'
                    ? formatRate(record.option.expirationRate)
                    : null,
        },
        {
            title: 'Remaining',
            key: 'remaining',
            onCell: restColOnCell,
            render: (_: unknown, record: FlatRow) =>
                record.kind === 'option' ? (
                    <RemainingCell row={record.option} />
                ) : null,
        },
    ];

    const columnCount = columns.length;
    columns[0].onCell = (record: FlatRow) =>
        record.kind === 'option' ? {} : { colSpan: columnCount };

    return columns;
};

export interface RewardsInventoryTableProps {
    tierLadder: CampaignTierLadderState;
    couponStock: CampaignCouponStockState;
    activity: CampaignPromotionActivityState;
    provinces: string[];
}

/**
 * One table, grouped by tier under its cumulative visit threshold. Always
 * the campaign-to-date window; the page Period never moves it. Region
 * still applies, same as the stock cards above.
 */
export const RewardsInventoryTable: React.FC<RewardsInventoryTableProps> = ({
    tierLadder,
    couponStock,
    activity,
    provinces,
}) => {
    const [openOverride, setOpenOverride] = useState<Set<number> | null>(null);

    if (
        tierLadder.status === 'loading' ||
        couponStock.status === 'loading' ||
        activity.status === 'loading'
    ) {
        return <Spin />;
    }
    if (tierLadder.status === 'unavailable') {
        return <DegradationNote reason={tierLadder.reason} />;
    }
    if (couponStock.status === 'unavailable') {
        return <DegradationNote reason={couponStock.reason} />;
    }
    if (activity.status === 'unavailable') {
        return <DegradationNote reason={activity.reason} />;
    }

    const scopedCoupons = couponsInRegion(couponStock.coupons, provinces);
    const catalogPromotionIds = new Set(
        couponStock.coupons.map((coupon) => coupon.id)
    );
    const tiers = buildRewardsInventory(
        tierLadder.tierRewards,
        scopedCoupons,
        activity.byPromotionId,
        activity.byCouponGroup,
        catalogPromotionIds
    );

    if (tiers.length === 0) {
        return (
            <p className={style.empty_tier}>
                No milestone reward tiers are configured.
            </p>
        );
    }

    // The first tier with rows to show, falling back to tiers[0] when a
    // Region filter has emptied every tier: never reads as an empty table.
    const defaultOpenTierId = (
        tiers.find((tier) => tier.rows.length > 0) ?? tiers[0]
    ).tierId;

    const currentTierIds = new Set(tiers.map((tier) => tier.tierId));
    const openTierIds = reconcileOpenTierIds(
        openOverride,
        currentTierIds,
        defaultOpenTierId
    );

    const toggleTier = (tierId: number) => {
        const next = new Set(openTierIds);
        if (next.has(tierId)) next.delete(tierId);
        else next.add(tierId);
        setOpenOverride(next);
    };

    return (
        <Table<FlatRow>
            className={style.table}
            tableLayout="fixed"
            scroll={{ x: 1100 }}
            rowKey={rowKeyOf}
            dataSource={buildFlatRows(tiers, openTierIds)}
            columns={buildColumns(toggleTier)}
            pagination={false}
            rowClassName={rowClassNameOf}
            onRow={(record) =>
                record.kind === 'group'
                    ? { onClick: () => toggleTier(record.tierId) }
                    : {}
            }
        />
    );
};
